/**
 * ViewPicker (Task B5) — the shared multi-select view-pill selector.
 *
 * Extracted verbatim (markup + CSS classes) from GeneratePanel's Views axis so BOTH
 * the customer Generate flow AND the admin TemplateEditor's "Generate views (AI)"
 * section render the exact same pills. Pure presentational: it holds NO state, calls
 * `onChange` with the next selection on every toggle, and enforces the `max` cap
 * itself (past the cap only already-selected pills stay togglable, to deselect).
 *
 * `view` is an opaque key — the consumer decides what it means (the customer flow
 * passes the option-choice id; the editor passes the view_type). Pass `Infinity` for
 * `max` when there is no cap.
 */
import { Icon } from "../ui/Icon.js";

export interface ViewPickerProps {
  /** The selectable views: an opaque `view` key + its human `label`, plus an OPTIONAL
   *  `thumb` reference image rendered in the pill (the customer flow passes its option
   *  choice's `reference_thumb`; the editor omits it). */
  views: { view: string; label: string; thumb?: string }[];
  /** The currently-selected `view` keys. */
  selected: string[];
  /** The maximum number of views selectable at once (`Infinity` = no cap). */
  max: number;
  /** Called with the FULL next selection whenever a pill is toggled. */
  onChange(next: string[]): void;
}

export function ViewPicker({ views, selected, max, onChange }: ViewPickerProps) {
  // At the cap, only already-selected pills stay togglable (so they can be deselected).
  const atCap = selected.length >= max;
  const toggle = (view: string) => {
    onChange(
      selected.includes(view) ? selected.filter((v) => v !== view) : [...selected, view],
    );
  };
  return (
    <div className="generate-panel__pills">
      {views.map(({ view, label, thumb }) => {
        const checked = selected.includes(view);
        const disabled = atCap && !checked;
        return (
          <label
            key={view}
            className={`chip view-pill${checked ? " chip--on is-selected" : ""}${disabled ? " chip--disabled is-disabled" : ""}`}
            data-testid={`axis-choice-${view}`}
          >
            <input
              type="checkbox"
              className="view-pill__input"
              checked={checked}
              disabled={disabled}
              onChange={() => toggle(view)}
              data-testid={`axis-input-${view}`}
            />
            <span className="view-pill__check" aria-hidden="true">
              {checked ? <Icon name="check" size={16} /> : <Icon name="plus" size={16} />}
            </span>
            {thumb && (
              <img className="view-pill__thumb" src={thumb} alt="" aria-hidden="true" />
            )}
            <span className="view-pill__label">{label}</span>
          </label>
        );
      })}
    </div>
  );
}
